import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, PiggyBank, Sparkles } from "lucide-react";
import { CountUp } from "@/components/common/CountUp";
import { useHousehold } from "@/context/HouseholdContext";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";

const Figure = ({ label, value, money, testid }) => (
  <div><div className="text-xs font-bold uppercase tracking-wide opacity-75">{label}</div>
    <div className="text-3xl font-extrabold sm:text-4xl" data-testid={testid}><CountUp value={value} format={(v) => money(v, { maximumFractionDigits: 0 })} /></div></div>
);

const Block = ({ tone, icon: Icon, title, children, testid }) => (
  <section className={`${tone} space-y-4 p-5`} data-testid={testid}>
    <h2 className="flex items-center gap-2 text-base font-extrabold"><Icon className="h-5 w-5" /> {title}</h2>
    {children}
  </section>
);

export const SavingsHero = () => {
  const { money } = useFormat();
  const { active } = useHousehold();
  const { data } = useQuery({ queryKey: ["savings-summary", active?.id], queryFn: () => api.get("/finance/savings-summary", { params: { household_id: active?.id } }).then((r) => r.data) });
  if (!data) return null;
  const { realised: r, possible: p } = data;
  return (
    <div className="stagger grid gap-4 md:grid-cols-2" data-testid="savings-hero">
      <Block tone="saving-strong" icon={PiggyBank} title="Économies réalisées" testid="savings-realised">
        <div className="grid grid-cols-2 gap-4"><Figure label="Ce mois-ci" value={r.month} money={money} testid="savings-realised-month" /><Figure label={`Depuis janvier`} value={r.year} money={money} testid="savings-realised-year" /></div>
        <p className="text-sm opacity-80">{r.month ? `Soit ${money(r.annual, { maximumFractionDigits: 0 })} par an grâce aux offres acceptées.` : "Acceptez une meilleure offre sur une charge pour commencer à économiser."}</p>
      </Block>
      <Block tone="decision-strong" icon={Sparkles} title="Économies possibles" testid="savings-possible">
        <div className="grid grid-cols-2 gap-4"><Figure label="Par mois" value={p.month} money={money} testid="savings-possible-month" /><Figure label="Sur l'année" value={p.year} money={money} testid="savings-possible-year" /></div>
        {data.top.length ? <ul className="space-y-1.5 text-sm">{data.top.map((o, i) => (
          <li key={`${o.label}-${i}`}><Link to={o.link} className="group flex items-center justify-between gap-2 rounded-lg bg-background/60 px-3 py-1.5 font-semibold hover:bg-background" data-testid={`savings-opportunity-${i}`}>
            <span className="truncate">{o.label}</span><span className="flex items-center gap-1 whitespace-nowrap">−{money(o.saving_monthly)}/mois <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span></Link></li>))}</ul>
          : <p className="text-sm opacity-80">Aucune économie détectée pour l'instant : bravo, vos charges sont optimisées.</p>}
      </Block>
    </div>
  );
};
